import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { buildApiUrl } from "@/lib/config";
import { Truck, Eye, ClipboardList, Clock, CheckCircle2, XCircle, FileText, ExternalLink } from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────────────

interface ForexDeliveryOrder {
  id: number;
  fullName: string;
  mobile: string;
  email: string;
  currencyCode: string;
  currencyAmount: string;
  inrEquivalent: string;
  exchangeRate: string;
  deliveryCharge: number;
  gstCharge: string;
  totalAmount: string;
  deliveryAddress: string;
  city: string;
  state: string;
  pinCode: string;
  preferredDeliveryDate: string;
  purpose: string | null;
  notes: string | null;
  documents: Array<{ type: string; url: string; filename: string }> | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

// ── Constants ──────────────────────────────────────────────────────────────

const STATUSES = ["Pending", "In Progress", "Completed", "Cancelled"] as const;

const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "outline" | "destructive"; color: string }> = {
  Pending:     { label: "Pending",     variant: "secondary",    color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  "In Progress": { label: "In Progress", variant: "default",   color: "bg-blue-100 text-blue-800 border-blue-200" },
  Completed:   { label: "Completed",   variant: "outline",      color: "bg-green-100 text-green-800 border-green-200" },
  Cancelled:   { label: "Cancelled",   variant: "destructive",  color: "bg-red-100 text-red-800 border-red-200" },
};

// ── Helper ─────────────────────────────────────────────────────────────────

const fmtINR = (val: string | number) => {
  const n = parseFloat(String(val));
  if (isNaN(n)) return "—";
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
};

const fmtDate = (val: string) => {
  try {
    return format(new Date(val), "dd MMM yyyy");
  } catch {
    return val;
  }
};

const fmtDateTime = (val: string) => {
  try {
    return format(new Date(val), "dd MMM yyyy, HH:mm");
  } catch {
    return val;
  }
};

// ── Detail Dialog ──────────────────────────────────────────────────────────

function OrderDetailDialog({ order }: { order: ForexDeliveryOrder }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="px-2 py-1">
          <Eye className="h-3 w-3 mr-1" />
          View
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-orange-500" />
            Order #{order.id} — {order.fullName}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <Section title="Customer Details">
            <Row label="Full Name" value={order.fullName} />
            <Row label="Mobile" value={order.mobile} />
            <Row label="Email" value={order.email} />
          </Section>

          <Section title="Order Details">
            <Row label="Currency" value={`${parseFloat(order.currencyAmount).toLocaleString("en-IN")} ${order.currencyCode}`} />
            <Row label="Exchange Rate" value={`1 ${order.currencyCode} = ₹${parseFloat(order.exchangeRate).toFixed(4)}`} />
            <Row label="INR Equivalent" value={fmtINR(order.inrEquivalent)} />
            <Row label="Delivery Charge" value={`₹${order.deliveryCharge}`} />
            <Row label="GST" value={fmtINR(order.gstCharge)} />
            <Row label="Total Payable" value={fmtINR(order.totalAmount)} highlight />
          </Section>

          <Section title="Delivery Details">
            <Row label="Address" value={order.deliveryAddress} />
            <Row label="City" value={order.city} />
            <Row label="State" value={order.state} />
            <Row label="PIN Code" value={order.pinCode} />
            <Row label="Preferred Date" value={fmtDate(order.preferredDeliveryDate)} />
            {order.purpose && <Row label="Purpose" value={order.purpose} />}
            {order.notes && <Row label="Notes" value={order.notes} />}
          </Section>

          {order.documents && order.documents.length > 0 && (
            <div>
              <p className="font-semibold text-gray-700 mb-2 border-b border-gray-100 pb-1">KYC Documents</p>
              <div className="space-y-2">
                {order.documents.map((doc) => (
                  <div key={doc.type} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-3 h-3 text-orange-500 shrink-0" />
                      <span className="text-gray-500 text-xs truncate">{doc.type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</span>
                    </div>
                    <a
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800 hover:underline shrink-0"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {doc.filename}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Section title="Request Info">
            <Row label="Status" value={order.status} />
            <Row label="Submitted" value={fmtDateTime(order.createdAt)} />
            <Row label="Last Updated" value={fmtDateTime(order.updatedAt)} />
          </Section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="font-semibold text-gray-700 mb-2 border-b border-gray-100 pb-1">{title}</p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span className={`text-right ${highlight ? "font-bold text-orange-700" : "text-gray-800"}`}>{value}</span>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

export default function ForexDeliveryOrders() {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: orders = [], isLoading } = useQuery<ForexDeliveryOrder[]>({
    queryKey: ["/api/forex-delivery/orders"],
    queryFn: async () => {
      const token = localStorage.getItem("authToken");
      const res = await fetch(buildApiUrl("api/forex-delivery/orders"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch orders");
      return res.json();
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }) => {
      const token = localStorage.getItem("authToken");
      const res = await fetch(buildApiUrl(`api/forex-delivery/orders/${id}/status`), {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error("Failed to update status");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/forex-delivery/orders"] });
      toast({ title: "Status updated", description: "Order status has been updated." });
    },
    onError: () => {
      toast({ title: "Update failed", description: "Could not update the order status.", variant: "destructive" });
    },
    onSettled: () => setUpdatingId(null),
  });

  const handleStatusChange = (id: number, status: string) => {
    setUpdatingId(id);
    updateStatusMutation.mutate({ id, status });
  };

  const filtered = statusFilter === "all"
    ? orders
    : orders.filter((o) => o.status === statusFilter);

  // Summary stats
  const total = orders.length;
  const pending = orders.filter((o) => o.status === "Pending").length;
  const inProgress = orders.filter((o) => o.status === "In Progress").length;
  const completed = orders.filter((o) => o.status === "Completed").length;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Total Orders</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{total}</p>
              </div>
              <ClipboardList className="w-6 h-6 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Pending</p>
                <p className="text-xl sm:text-2xl font-bold text-yellow-600">{pending}</p>
              </div>
              <Clock className="w-6 h-6 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">In Progress</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-600">{inProgress}</p>
              </div>
              <Truck className="w-6 h-6 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-medium text-gray-600">Completed</p>
                <p className="text-xl sm:text-2xl font-bold text-green-600">{completed}</p>
              </div>
              <CheckCircle2 className="w-6 h-6 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Orders table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between flex-wrap gap-4">
            <span className="flex items-center gap-2">
              <Truck className="w-5 h-5 text-orange-500" />
              Home Delivery Requests
            </span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-normal text-gray-500">Filter:</span>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10">
              <Truck className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">
                {statusFilter === "all"
                  ? "No home delivery orders yet."
                  : `No orders with status "${statusFilter}".`}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="min-w-[50px]">ID</TableHead>
                    <TableHead className="min-w-[130px]">Customer</TableHead>
                    <TableHead className="min-w-[110px] hidden sm:table-cell">Mobile</TableHead>
                    <TableHead className="min-w-[90px]">Currency</TableHead>
                    <TableHead className="min-w-[100px] hidden md:table-cell">INR Equiv.</TableHead>
                    <TableHead className="min-w-[80px] hidden md:table-cell">Charge</TableHead>
                    <TableHead className="min-w-[110px] hidden lg:table-cell">Total</TableHead>
                    <TableHead className="min-w-[90px] hidden md:table-cell">City</TableHead>
                    <TableHead className="min-w-[100px] hidden lg:table-cell">Pref. Date</TableHead>
                    <TableHead className="min-w-[130px]">Status</TableHead>
                    <TableHead className="min-w-[110px] hidden sm:table-cell">Submitted</TableHead>
                    <TableHead className="min-w-[70px]">Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((order) => {
                    const cfg = statusConfig[order.status] ?? statusConfig["Pending"];
                    return (
                      <TableRow key={order.id}>
                        <TableCell className="text-xs text-gray-500">#{order.id}</TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm font-medium text-gray-900 truncate max-w-[120px]">{order.fullName}</p>
                            <p className="text-xs text-gray-500 truncate max-w-[120px]">{order.email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs hidden sm:table-cell">{order.mobile}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono text-xs">
                            {parseFloat(order.currencyAmount).toLocaleString("en-IN")} {order.currencyCode}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs hidden md:table-cell">{fmtINR(order.inrEquivalent)}</TableCell>
                        <TableCell className="text-xs hidden md:table-cell">₹{order.deliveryCharge}</TableCell>
                        <TableCell className="text-xs font-semibold text-orange-700 hidden lg:table-cell">{fmtINR(order.totalAmount)}</TableCell>
                        <TableCell className="text-xs hidden md:table-cell">{order.city}</TableCell>
                        <TableCell className="text-xs hidden lg:table-cell">{fmtDate(order.preferredDeliveryDate)}</TableCell>
                        <TableCell>
                          <Select
                            value={order.status}
                            onValueChange={(val) => handleStatusChange(order.id, val)}
                            disabled={updatingId === order.id}
                          >
                            <SelectTrigger className={`h-7 text-xs w-32 border ${cfg.color}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((s) => (
                                <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-xs text-gray-500 hidden sm:table-cell">
                          {fmtDateTime(order.createdAt)}
                        </TableCell>
                        <TableCell>
                          <OrderDetailDialog order={order} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
